import * as R from "ramda";
import { useCallback, useState } from "react";

import { TestLensParams, testLensWithClaude } from "../../../data/queries/lens-tester";
import { AvailableModel } from "../../../services/claude";
import { logError } from "../../../utils/error";

type ArtifactData = {
  transcript?: {
    content?: {
      transcription?: string;
    };
  };
  callTranscriptionRecord?: {
    content?: Record<string, unknown>;
  };
};

type TestLensConfig = {
  testLens: string;
  model: AvailableModel;
  tokens: number;
  temperature: number;
  companyName: string;
  companyDescription: string;
  lensName: string;
};

type ValidationResult = {
  isValid: boolean;
  error?: string;
};

type ExtractedData = {
  transcript: string;
  callDataRecord: Record<string, unknown>;
};

interface LensAnalysisItem {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  score_value?: number;
}

interface LensResult {
  analysis?: LensAnalysisItem[];
  // eslint-disable-next-line @typescript-eslint/naming-convention
  overall_score?: number;
}

const SCORE_DECIMAL_PLACES = 2;

interface UseLensTestReturn {
  testResults: unknown;
  isLoading: boolean;
  error: string | null;
  testLens: (config: TestLensConfig, artifactData: ArtifactData) => Promise<unknown | null>;
  clearResults: () => void;
}

const validateArtifactData = (artifactData: ArtifactData): ValidationResult => {
  if (!artifactData) {
    return { isValid: false, error: "No artifact data available for testing" };
  }
  return { isValid: true };
};

const extractTranscriptData = (artifactData: ArtifactData): ExtractedData => {
  const transcript = artifactData.transcript?.content?.transcription || "";
  const callDataRecord = artifactData.callTranscriptionRecord?.content || {};

  if (!transcript) {
    throw new Error("No transcript data available for testing");
  }

  return { transcript, callDataRecord };
};

const fixOverallScoreCalculation = (result: LensResult): LensResult =>
  R.pipe(
    R.propOr([], "analysis"),
    R.pluck("score_value"),
    //@ts-expect-error - R.identity is not typed
    R.filter(R.identity),
    R.mean,
    (average) => R.assoc("overall_score", Number(average.toFixed(SCORE_DECIMAL_PLACES)), result)
  )(result);

const executeTestWithClaude = async (
  config: TestLensConfig,
  extractedData: ExtractedData
): Promise<unknown> => {
  const testParams: TestLensParams = {
    testLens: config.testLens,
    transcript: extractedData.transcript,
    callDataRecord: extractedData.callDataRecord,
    companyName: config.companyName,
    companyDescription: config.companyDescription,
    model: config.model,
    tokens: config.tokens,
    temperature: config.temperature
  };

  const { success, data: result, error } = await testLensWithClaude(testParams);

  if (!success) {
    logError("Failed to test lens", error);
    return null;
  }

  return fixOverallScoreCalculation(result as LensResult);
};

const useLensTest = (): UseLensTestReturn => {
  const [testResults, setTestResults] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const testLens = useCallback(async (
    config: TestLensConfig,
    artifactData: ArtifactData
  ): Promise<unknown | null> => {
    const artifactValidation = validateArtifactData(artifactData);
    if (!artifactValidation.isValid) {
      setError(artifactValidation.error!);
      return null;
    }

    setIsLoading(true);
    setError(null);
    setTestResults(null);

    try {
      const extractedData = extractTranscriptData(artifactData);
      const result = await executeTestWithClaude(config, extractedData);
      setTestResults(result);
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to test lens";
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearResults = useCallback((): void => {
    setTestResults(null);
    setError(null);
  }, []);

  return {
    testResults,
    isLoading,
    error,
    testLens,
    clearResults
  };
};

export { useLensTest };
export type { ArtifactData, TestLensConfig };
